/**
 * melDate.js — pure calendar maths on ISO date strings (YYYY-MM-DD).
 *
 * "Today" always comes from the server (/api/casual/access -> today, computed
 * with ZoneInfo("Australia/Melbourne")). melTodayISO() is only the fallback
 * while that request is in flight, and it also resolves in Melbourne time.
 *
 * Everything works in UTC-midnight Date objects so no local-timezone
 * rollover can shift a date by a day.
 */

export const MEL_TZ = 'Australia/Melbourne';
export const DAY_NAMES = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
export const DAY_FULL = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

export const melTodayISO = () =>
  new Intl.DateTimeFormat('en-CA', {
    timeZone: MEL_TZ, year: 'numeric', month: '2-digit', day: '2-digit',
  }).format(new Date()); // en-CA => YYYY-MM-DD

export const parseISO = (iso) => {
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d));
};
export const toISO = (date) => date.toISOString().slice(0, 10);

export const addDays = (iso, n) => {
  const d = parseISO(iso);
  d.setUTCDate(d.getUTCDate() + n);
  return toISO(d);
};

/** 0 = Mon … 6 = Sun */
export const dayIndex = (iso) => (parseISO(iso).getUTCDay() + 6) % 7;
export const dayName = (iso) => DAY_NAMES[dayIndex(iso)];
export const dayFull = (iso) => DAY_FULL[dayIndex(iso)];

export const startOfWeek = (iso) => addDays(iso, -dayIndex(iso));
export const endOfWeek = (iso) => addDays(startOfWeek(iso), 6);
export const endOfMonth = (iso) => {
  const d = parseISO(iso);
  return toISO(new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + 1, 0)));
};

export const datesBetween = (a, b) => {
  const out = [];
  let cur = a;
  while (cur <= b) { out.push(cur); cur = addDays(cur, 1); }
  return out;
};

export const fmtShort = (iso) => {
  const d = parseISO(iso);
  return `${DAY_NAMES[dayIndex(iso)]} ${d.getUTCDate()} ${MONTHS[d.getUTCMonth()]}`;
};
export const fmtDayNum = (iso) => String(parseISO(iso).getUTCDate());
export const fmtRange = (a, b) => (a === b ? fmtShort(a) : `${fmtShort(a)} – ${fmtShort(b)}`);
export const fmtMonthYear = (iso) => {
  const d = parseISO(iso);
  return `${MONTHS[d.getUTCMonth()]} ${d.getUTCFullYear()}`;
};

/* ── mode → blocks ─────────────────────────────────────────────
   A block is one accordion panel / one table row group:
   { id, label, start, end, dates: [iso…] }
   Today / Tomorrow / week modes produce one block; month produces one per
   remaining Mon–Sun week. Only dates from today onward are ever included. */
export const MODES = [
  { id: 'today',     label: 'Today' },
  { id: 'tomorrow',  label: 'Tomorrow' },
  { id: 'week',      label: 'Current Week' },
  { id: 'nextweek',  label: 'Next Week' },
  { id: 'month',     label: 'Current Month' },
];

export const buildBlocks = (mode, today) => {
  switch (mode) {
    case 'today':
      return [{ id: 'b0', label: 'Today', start: today, end: today, dates: [today] }];
    case 'tomorrow': {
      const t = addDays(today, 1);
      return [{ id: 'b0', label: 'Tomorrow', start: t, end: t, dates: [t] }];
    }
    case 'week': {
      const end = endOfWeek(today);
      return [{ id: 'b0', label: 'This week', start: today, end, dates: datesBetween(today, end) }];
    }
    case 'nextweek': {
      const start = addDays(startOfWeek(today), 7);
      const end = addDays(start, 6);
      return [{ id: 'b0', label: 'Next week', start, end, dates: datesBetween(start, end) }];
    }
    case 'month': {
      const monthEnd = endOfMonth(today);
      const blocks = [];
      let cur = today;
      let n = 1;
      while (cur <= monthEnd) {
        const end = endOfWeek(cur) < monthEnd ? endOfWeek(cur) : monthEnd;
        blocks.push({ id: `b${n - 1}`, label: `Week ${n}`, start: cur, end, dates: datesBetween(cur, end) });
        cur = addDays(end, 1);
        n += 1;
      }
      return blocks;
    }
    default:
      return [];
  }
};

/* ── time options: 30-minute steps across the day ──────────── */
const pad = (n) => String(n).padStart(2, '0');
export const to12h = (hhmm) => {
  const [h, m] = hhmm.split(':').map(Number);
  const suffix = h < 12 ? 'AM' : 'PM';
  const hr = h % 12 === 0 ? 12 : h % 12;
  return `${hr}:${pad(m)} ${suffix}`;
};
export const TIME_OPTIONS = Array.from({ length: 48 }, (_, i) => `${pad(Math.floor(i / 2))}:${i % 2 ? '30' : '00'}`);
export const TIME_LABELS = TIME_OPTIONS.map(to12h);
export const labelToTime = (label) => TIME_OPTIONS[TIME_LABELS.indexOf(label)] || '';
export const timeToLabel = (hhmm) => (hhmm ? to12h(hhmm) : '');